"use client";

import { useEffect, useRef, useState } from "react";

type PostingGroupFilter = "all" | "3" | "2" | "1";

type PsleTableKind = "sec" | "ip" | "no-range" | null;

export function classifyPsleTable(headers: string[]): PsleTableKind {
  const normalized = headers.map((header) => header.trim().toLowerCase());
  if (normalized.includes("pg3 non-affiliated")) return "sec";
  if (
    normalized.length === 3 &&
    normalized.includes("non-affiliated") &&
    normalized.includes("affiliated")
  ) {
    return "ip";
  }
  if (normalized.includes("ordinary s1 posting")) return "no-range";
  return null;
}

function tableHeaders(table: HTMLTableElement): string[] {
  return Array.from(table.querySelectorAll("thead th")).map(
    (cell) => cell.textContent ?? ""
  );
}

function findPsleTables(article: HTMLElement): Array<{
  table: HTMLTableElement;
  kind: Exclude<PsleTableKind, null>;
}> {
  return Array.from(article.querySelectorAll("table")).flatMap((table) => {
    const kind = classifyPsleTable(tableHeaders(table));
    return kind ? [{ table, kind }] : [];
  });
}

function setCellVisible(cell: HTMLTableCellElement, visible: boolean): void {
  cell.style.display = visible ? "" : "none";
}

export default function SecondaryPsleScoreExplorer() {
  const markerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [postingGroup, setPostingGroup] = useState<PostingGroupFilter>("all");
  const [matchCount, setMatchCount] = useState<number | null>(null);

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) {
      setPostingGroup("3");
    }
  }, []);

  useEffect(() => {
    const article = markerRef.current?.closest("article");
    if (!(article instanceof HTMLElement)) return;

    const tables = findPsleTables(article);
    const normalizedQuery = query.trim().toLocaleLowerCase("en-SG");
    const matchedSchools = new Set<string>();

    for (const { table, kind } of tables) {
      table.id = `secondary-psle-${kind}-table`;
      table.classList.add("psle-score-table");
      table
        .closest(".md-table-scroll")
        ?.classList.add("psle-score-table-scroll");

      const rows = Array.from(
        table.querySelectorAll<HTMLTableRowElement>("tbody tr")
      );
      for (const row of rows) {
        const schoolName = row.cells[0]?.textContent?.trim() ?? "";
        const matches =
          normalizedQuery === "" ||
          schoolName.toLocaleLowerCase("en-SG").includes(normalizedQuery);
        row.hidden = !matches;
        if (matches && schoolName) matchedSchools.add(schoolName);
      }

      if (kind !== "sec") continue;
      const groupColumns: Record<
        Exclude<PostingGroupFilter, "all">,
        number[]
      > = {
        "3": [1, 2],
        "2": [3, 4],
        "1": [5, 6],
      };
      const visibleColumns =
        postingGroup === "all" ? null : groupColumns[postingGroup];
      for (const row of Array.from(table.rows)) {
        for (const [index, cell] of Array.from(row.cells).entries()) {
          setCellVisible(
            cell,
            index === 0 ||
              visibleColumns === null ||
              visibleColumns.includes(index)
          );
        }
      }
    }

    setMatchCount(matchedSchools.size);

    return () => {
      for (const { table } of tables) {
        for (const row of Array.from(
          table.querySelectorAll<HTMLTableRowElement>("tbody tr")
        )) {
          row.hidden = false;
        }
        for (const row of Array.from(table.rows)) {
          for (const cell of Array.from(row.cells)) setCellVisible(cell, true);
        }
      }
    };
  }, [postingGroup, query]);

  const jumpTo = (kind: Exclude<PsleTableKind, null>) => {
    document
      .getElementById(`secondary-psle-${kind}-table`)
      ?.closest(".md-table-scroll")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      ref={markerRef}
      className="not-prose my-6 rounded-2xl border border-eclat-200 bg-eclat-50/70 p-4 sm:p-5"
      aria-label="PSLE score range table controls"
    >
      <h3 className="m-0 text-lg font-semibold text-slate-950">
        Find a school in the official table
      </h3>
      <p className="mt-1 text-sm leading-6 text-slate-600">
        Search by school name. On a narrow screen, show one Posting Group at a
        time so the school name and both affiliation columns remain visible.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_13rem]">
        <label className="text-sm font-medium text-slate-800">
          School name
          <input
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-950 shadow-sm focus:border-eclat-600 focus:outline-none focus:ring-2 focus:ring-eclat-200"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="For example, Catholic High"
            type="search"
            value={query}
          />
        </label>
        <label className="text-sm font-medium text-slate-800">
          SEC Posting Group
          <select
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-950 shadow-sm focus:border-eclat-600 focus:outline-none focus:ring-2 focus:ring-eclat-200"
            onChange={(event) =>
              setPostingGroup(event.target.value as PostingGroupFilter)
            }
            value={postingGroup}
          >
            <option value="all">Show all groups</option>
            <option value="3">Posting Group 3</option>
            <option value="2">Posting Group 2</option>
            <option value="1">Posting Group 1</option>
          </select>
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm text-slate-600">
          {matchCount === null
            ? "Preparing tables"
            : `${matchCount} matching ${
                matchCount === 1 ? "school" : "schools"
              }`}
        </span>
        <button
          className="rounded-full border border-eclat-300 bg-white px-3 py-1.5 text-sm font-semibold text-eclat-800 hover:bg-eclat-100"
          onClick={() => jumpTo("sec")}
          type="button"
        >
          SEC table
        </button>
        <button
          className="rounded-full border border-eclat-300 bg-white px-3 py-1.5 text-sm font-semibold text-eclat-800 hover:bg-eclat-100"
          onClick={() => jumpTo("ip")}
          type="button"
        >
          IP table
        </button>
        <button
          className="rounded-full border border-eclat-300 bg-white px-3 py-1.5 text-sm font-semibold text-eclat-800 hover:bg-eclat-100"
          onClick={() => jumpTo("no-range")}
          type="button"
        >
          Other admission routes
        </button>
      </div>
    </div>
  );
}
